import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import useMarketConfig from '@clearscore/hooks.use-market-config';
import AppBackground from '@shared/app-background';
import ConnectedAlert from '@shared/alert';
import getEnv from '@clearscore/helpers.envs';
import { constants } from '@clearscore/redux.market';
import LazyLoadComponent from '@scaffolding/lazy-component';
import monitoring from '@clearscore/helpers.monitoring';
import { ACCOUNT_STATUSES } from '@clearscore/constants.profile-statuses';

import Header from '../../components/header';
import MobileNavigationBar from '../../components/mobile-navigation-bar';
import marketConfig from '../../libs/market-config';

const AppTemplate = ({
    profile,
    dashboard,
    market,
    currentMarketFetchStatus,
    fetchProfile,
    fetchDashboard,
    route,
    parentState,
    parentDispatch,
    match,
    coreAppBridgeTypes,
    reportId,
    idvStatus,
}) => {
    const { routeProps = {} } = route;
    const { background = {} } = routeProps;
    const { hasBurgerMenu } = useMarketConfig(marketConfig);
    const isNativeWebview = getEnv('WEBVIEW');

    useEffect(() => {
        if (profile.isInitial && market !== constants.GLOBAL && currentMarketFetchStatus.isComplete) {
            fetchProfile();
        }
    }, [currentMarketFetchStatus.isComplete, fetchProfile, market, profile.isInitial]);

    useEffect(() => {
        if (dashboard.isInitial && market !== constants.GLOBAL && currentMarketFetchStatus.isComplete) {
            fetchDashboard();
        }
    }, [currentMarketFetchStatus.isComplete, fetchDashboard, market, dashboard.isInitial]);

    if (!dashboard.isComplete || !profile.isComplete) {
        return null;
    }

    const hasDefectedReport = !!(!reportId && idvStatus === ACCOUNT_STATUSES.PASS);

    if (monitoring.hasMonitoring() && hasDefectedReport && !dashboard.isInitial && !dashboard.isPending) {
        monitoring.log(`CS-24637 - Defected Report: Market: ${market}`);
    }

    return (
        <Fragment>
            <AppBackground {...background} />

            <If condition={!isNativeWebview}>
                <Header
                    hasBurgerMenu={hasBurgerMenu && !hasDefectedReport}
                    routerTrackingSource="global-navigation"
                    hasDefectedReport={hasDefectedReport}
                />
            </If>

            <LazyLoadComponent
                component={{
                    ...route,
                    computedMatch: match,
                    parentProps: {
                        initialState: parentState,
                        dispatch: parentDispatch,
                        coreAppBridgeTypes,
                    },
                }}
            />

            <If condition={!isNativeWebview && !hasBurgerMenu && !hasDefectedReport}>
                <MobileNavigationBar hasBlurredBackground={!!background.focus} />
            </If>

            <ConnectedAlert dataQa="app-template-alert" isBelowHeader />
        </Fragment>
    );
};

AppTemplate.propTypes = {
    profile: PropTypes.object.isRequired,
    dashboard: PropTypes.object.isRequired,
    fetchProfile: PropTypes.func.isRequired,
    fetchDashboard: PropTypes.func.isRequired,
    market: PropTypes.string.isRequired,
    currentMarketFetchStatus: PropTypes.shape({
        isComplete: PropTypes.bool.isRequired,
    }).isRequired,
    route: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    parentState: PropTypes.object.isRequired,
    parentDispatch: PropTypes.func.isRequired,
    coreAppBridgeTypes: PropTypes.array.isRequired,
    reportId: PropTypes.string,
    idvStatus: PropTypes.string,
};

AppTemplate.defaultProps = {
    reportId: null,
    idvStatus: null,
};

export default AppTemplate;
